import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import {
  User,
  LoginResponse,
  LoginEmailRequest,
  ActivateAccountRequest,
  TwoFactorType,
  UserRole,
} from '@shared/models';

import { AnalyticsService } from './analytics.service';
import { AuthHttpService } from './http-client';
import { LocalStorageService } from './local-storage.service';
import { NotificationService } from './notification.service';
import { ToastsService } from './toasts.service';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private get token(): string | null {
    return this.tokenService.token;
  }

  get user(): User | undefined {
    return this.tokenService.user;
  }

  get user$(): Observable<User | null> {
    return this.tokenService.user$;
  }

  get userTwoFactorType(): TwoFactorType {
    return this.tokenService.userTwoFactorType;
  }

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private tokenService: TokenService,
    private toastsService: ToastsService,
    private authHttpService: AuthHttpService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private analyticsService: AnalyticsService
  ) {
    this.loadUserData();
  }

  private loadUserData() {
    if (this.isUserLoggedIn()) {
      this.notificationService.refreshNotificationsInfo();
      setTimeout(() => this.notificationService.requestPermission(), 1000);
      this.analyticsService.setUser(this.user);
    }
  }

  login(loader: string, email: string, password: string, twoFactorCode?: string): Observable<LoginResponse | null> {
    return this.authHttpService
      .login(loader, new LoginEmailRequest(email, password, twoFactorCode))
      .pipe(map((response) => this.saveUser(response)));
  }

  refreshToken(loader: string): Observable<LoginResponse | null> {
    return this.authHttpService
      .refreshToken(loader, this.tokenService.refreshToken)
      .pipe(map((response) => this.saveUser(response)));
  }

  activateAccount(loader: string, dto: ActivateAccountRequest): Observable<LoginResponse | null> {
    return this.authHttpService.activateAccount(loader, dto).pipe(map((response) => this.saveUser(response)));
  }

  reloadCurrentUser(loader: string): void {
    this.authHttpService.getCurrentUser(loader).subscribe({
      next: (user) => {
        const userInfo = this.localStorageService.load(LocalStorageService.AUTH_USER_INFO);
        userInfo.user = user;
        this.tokenService.setUser(userInfo);
        this.localStorageService.store(LocalStorageService.AUTH_USER_INFO, userInfo);
      },
      error: (error) => this.toastsService.showError(error),
    });
  }

  getAuthorizationToken(): string {
    if (!this.token) {
      return '';
    }
    return this.token.toString();
  }

  setCurrentCompany(loader: string, companyId: string) {
    if (companyId === this.user?.companyId) {
      return of(null);
    }

    return this.authHttpService
      .switchUserLoginCompany(loader, companyId)
      .pipe(tap((response) => this.saveUser(response)));
  }

  isUserInRole(role: UserRole): boolean {
    return this.tokenService.isUserInRole(role);
  }

  isUserInOneOfRoles(roles: UserRole[]): boolean {
    return this.tokenService.isUserInOneOfRoles(roles);
  }

  isUserLoggedIn(): boolean {
    return this.tokenService.isUserLoggedIn();
  }

  logout(): void {
    this.notificationService.stopListening();
    this.tokenService.setUser(null);
    this.router.navigate(['auth', 'login']);
  }

  private saveUser(response: LoginResponse): LoginResponse | null {
    if (response.twoFactorType && !response.user) {
      return response;
    }
    if (response?.token?.accessToken && response?.user) {
      this.notificationService.requestPermission();
      this.tokenService.setUser(response);
      this.navigateByUserRole();
      this.loadUserData();
      return response;
    }
    return null;
  }

  navigateByUserRole(): void {
    if (this.isUserLoggedIn()) {
      if (this.isUserInRole(UserRole.Worker)) {
        this.router.navigate(['worker']);
        return;
      }
      this.router.navigate(['/']);
      return;
    }

    this.toastsService.showError(this.translate.instant('error.unauthorized'));
    this.logout();
  }

  acceptTermsAndConditions(): void {
    const userInfo = this.localStorageService.load(LocalStorageService.AUTH_USER_INFO) as LoginResponse;
    userInfo.user.termsAccepted = true;
    this.tokenService.setUser(userInfo);
    this.localStorageService.store(LocalStorageService.AUTH_USER_INFO, userInfo);
  }
}
